import React from 'react';
import { motion } from 'framer-motion';
import { REPORT_TYPES } from '../../types/reportTypes';

interface ReportTypeSelectorProps {
  selectedReportType: string;
  onReportTypeChange: (reportTypeId: string) => void;
}

const categoryColors = {
  respiratory: 'bg-blue-100 text-blue-800 border-blue-200',
  gastrointestinal: 'bg-green-100 text-green-800 border-green-200',
  urinary: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  wound: 'bg-red-100 text-red-800 border-red-200'
};

const categoryIcons = {
  respiratory: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
  ),
  gastrointestinal: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
    </svg>
  ),
  urinary: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
    </svg>
  ),
  wound: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
    </svg>
  )
};

const ReportTypeSelector: React.FC<ReportTypeSelectorProps> = ({
  selectedReportType,
  onReportTypeChange
}) => {
  const selectedReport = REPORT_TYPES.find(type => type.id === selectedReportType);

  return (
    <div className="mb-8">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-primary mb-2">Select PCR Panel Type</h3>
        <p className="text-text">Choose from our comprehensive range of molecular diagnostic panels</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {REPORT_TYPES.map((reportType) => (
          <motion.button
            key={reportType.id}
            onClick={() => onReportTypeChange(reportType.id)}
            className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
              selectedReportType === reportType.id
                ? 'border-primary bg-primary/5 shadow-md'
                : 'border-border bg-background-alt hover:border-primary/50 hover:shadow-sm'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${categoryColors[reportType.category]}`}>
                <span className="mr-1">{categoryIcons[reportType.category]}</span>
                {reportType.category.charAt(0).toUpperCase() + reportType.category.slice(1)}
              </div>
              <span className="text-xs text-secondary font-mono bg-background px-2 py-1 rounded">
                {reportType.panelSize} tests
              </span>
            </div>
            
            <h4 className="font-semibold text-text mb-2 text-sm leading-tight">
              {reportType.name}
            </h4>
            
            <p className="text-xs text-secondary mb-3 line-clamp-2">
              {reportType.description}
            </p>
            
            <div className="flex items-center text-xs text-secondary">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {reportType.specimenType}
            </div>
          </motion.button>
        ))}
      </div>

      {selectedReport && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mt-6 p-4 bg-background-alt rounded-lg border border-border"
        >
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-primary">{selectedReport.name}</h4>
              <p className="text-sm text-text mt-1">{selectedReport.description}</p>
            </div>
            <div className="text-right">
              <div className="text-sm text-secondary">Panel Size</div>
              <div className="text-lg font-bold text-primary">{selectedReport.panelSize}</div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ReportTypeSelector; 